import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { BASE } from './api';

type Listener = () => void;
const listeners = new Set<Listener>();

function current(): { path: string; query: URLSearchParams } {
  const p = location.pathname.startsWith(BASE) ? location.pathname.slice(BASE.length) : location.pathname;
  return { path: p.replace(/\/+$/, '') || '/', query: new URLSearchParams(location.search) };
}

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  const url = to.startsWith(BASE) ? to : `${BASE}${to.startsWith('/') ? to : `/${to}`}`;
  if (opts.replace) history.replaceState(null, '', url);
  else history.pushState(null, '', url);
  listeners.forEach((l) => l());
  if (!opts.replace) window.scrollTo(0, 0);
}

window.addEventListener('popstate', () => listeners.forEach((l) => l()));

/** Sends an old address to its new one, keeping the query, without adding a history entry. */
export function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(`${to}${location.search}`, { replace: true }), [to]);
  return null;
}

export function useLocation() {
  const [loc, setLoc] = useState(current);
  useEffect(() => {
    const l = () => setLoc(current());
    listeners.add(l);
    // A child's effect (e.g. <Redirect>) runs before this one and can navigate before we subscribe.
    const now = current();
    if (now.path !== loc.path || now.query.toString() !== loc.query.toString()) setLoc(now);
    return () => void listeners.delete(l);
  }, []);
  return loc;
}

/** Matches "/c/:id/:slug?" style patterns. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const pp = pattern.split('/').filter(Boolean);
  const sp = path.split('/').filter(Boolean);
  const out: Record<string, string> = {};
  for (let i = 0; i < Math.max(pp.length, sp.length); i++) {
    const p = pp[i];
    const s = sp[i];
    if (p === undefined) return null;
    if (p.startsWith(':')) {
      const optional = p.endsWith('?');
      const name = p.slice(1).replace(/\?$/, '');
      if (s === undefined) {
        if (optional) continue;
        return null;
      }
      out[name] = decodeURIComponent(s);
    } else if (p !== s) return null;
  }
  return out;
}

export function Link(props: { href: string; children: ComponentChildren } & Omit<JSX.HTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const { href, children, onClick, ...rest } = props;
  const full = `${BASE}${href}`;
  return (
    <a
      href={full}
      {...rest}
      onClick={(e) => {
        (onClick as any)?.(e);
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(href);
      }}
    >
      {children}
    </a>
  );
}
